import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMsg } from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Flame, ThermometerSun, Snowflake, Crown, Activity, MapPin, TrendingUp,
  Eye, Heart, MessageSquare, Phone, Filter, ChevronRight, X, Calendar, User, Sparkles,
  Inbox, UserCog, Trash2, Building2, PackagePlus, RefreshCw, Briefcase, Lightbulb, FileEdit,
} from "lucide-react";
import AdminEnquiriesTab from "./AdminEnquiriesTab";
import { BulkSelectBar, RowCheckbox } from "../components/BulkSelectBar";
import { formatTime } from "../lib/dateFmt";

/**
 * v2.40 — Admin Smart CRM Tab
 * v2.47 — Merged "Enquiries (form submissions)" + behavioural CRM + team-assign
 *         into ONE unified tab. Standalone Leads tab is now retired.
 *
 * Behavioural lead intelligence dashboard. Shows:
 *  - Temperature distribution (Cold / Warm / Hot / High-Intent)
 *  - Status funnel (New → Contacted → … → Closed/Lost)
 *  - Top areas (last 30 days) and top viewed listings
 *  - Searchable / filterable lead list with score, temperature, intent profile
 *  - Click any lead → side drawer with full timeline + status edit + notes + team-assign
 *  - Sub-tab: "Form Enquiries" — direct form submissions, also team-assignable
 */

const TEMP_META = {
  cold: { label: "Cold", Icon: Snowflake, ring: "ring-zinc-200", chip: "bg-zinc-100 text-zinc-700 border-zinc-200", color: "#71717A" },
  warm: { label: "Warm", Icon: ThermometerSun, ring: "ring-amber-200", chip: "bg-amber-50 text-amber-800 border-amber-200", color: "#D97706" },
  hot: { label: "Hot", Icon: Flame, ring: "ring-red-200", chip: "bg-red-50 text-red-800 border-red-200", color: "#DC2626" },
  high_intent: { label: "High Intent", Icon: Crown, ring: "ring-violet-200", chip: "bg-violet-50 text-violet-800 border-violet-200", color: "#6D28D9" },
};

const STATUS_META = {
  new: { label: "New", chip: "bg-blue-50 text-blue-800 border-blue-200" },
  contacted: { label: "Contacted", chip: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  follow_up: { label: "Follow-Up", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  interested: { label: "Interested", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  site_visit: { label: "Site Visit", chip: "bg-purple-50 text-purple-800 border-purple-200" },
  negotiation: { label: "Negotiation", chip: "bg-violet-50 text-violet-800 border-violet-200" },
  closed: { label: "Closed", chip: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  lost: { label: "Lost", chip: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

const EVENT_META = {
  login: { Icon: User, color: "#0055FF", label: "Logged in" },
  view: { Icon: Eye, color: "#0055FF", label: "Viewed property" },
  view_repeat: { Icon: Eye, color: "#D97706", label: "Re-viewed property" },
  save: { Icon: Heart, color: "#DC2626", label: "Saved property" },
  save_remove: { Icon: Heart, color: "#A1A1AA", label: "Removed save" },
  whatsapp_click: { Icon: MessageSquare, color: "#16A34A", label: "WhatsApp click" },
  call_intent: { Icon: Phone, color: "#0055FF", label: "Phone CTA" },
  callback_req: { Icon: Phone, color: "#DC2626", label: "Requested callback" },
  enquiry: { Icon: MessageSquare, color: "#DC2626", label: "Submitted enquiry" },
  filter_use: { Icon: Filter, color: "#71717A", label: "Used filters" },
  long_dwell: { Icon: Activity, color: "#6D28D9", label: "Long dwell" },
  project_view: { Icon: TrendingUp, color: "#6D28D9", label: "Viewed project" },
  search: { Icon: Filter, color: "#71717A", label: "Searched" },
  compare: { Icon: TrendingUp, color: "#6D28D9", label: "Compared listings" },
  // v2.69 — Internal actions (dealer / builder / team) — surfaced in the
  // "Internal Actions" section of the lead drawer.
  property_submitted: { Icon: PackagePlus, color: "#0F766E", label: "Submitted property", internal: true },
  property_updated: { Icon: FileEdit, color: "#0F766E", label: "Updated property", internal: true },
  project_created: { Icon: Building2, color: "#7C3AED", label: "Created project", internal: true },
  project_updated: { Icon: RefreshCw, color: "#7C3AED", label: "Updated project", internal: true },
  plan_purchased: { Icon: Briefcase, color: "#B45309", label: "Purchased plan", internal: true },
  team_lead_status_changed: { Icon: UserCog, color: "#0EA5E9", label: "Team status update", internal: true },
  team_lead_note: { Icon: MessageSquare, color: "#0EA5E9", label: "Team note", internal: true },
  team_lead_suggested_property: { Icon: Lightbulb, color: "#0EA5E9", label: "Team suggested property", internal: true },
};

export default function LeadsCRMTab() {
  const [analytics, setAnalytics] = useState(null);
  const [tempFilter, setTempFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [view, setView] = useState("behavioural");  // v2.47 — "behavioural" | "enquiries"
  // Team list cached at parent so the drawer + auto-assign both can use it
  const [team, setTeam] = useState([]);
  // v2.48 — Bulk select state for behavioural leads
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // v2.70 Phase 2 — Today's Tasks data
  const [todayTasks, setTodayTasks] = useState({ overdue: [], today: [], total: 0 });

  const loadAnalytics = () => {
    api.get("/admin/leads/analytics").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
  };
  const loadTodayTasks = () => {
    api.get("/admin/leads-today")
      .then((r) => setTodayTasks(r.data || { overdue: [], today: [], total: 0 }))
      .catch(() => setTodayTasks({ overdue: [], today: [], total: 0 }));
  };
  const loadLeads = (silent = false) => {
    if (silent !== true) setLoading(true);
    const params = new URLSearchParams();
    if (tempFilter) params.set("temperature", tempFilter);
    if (statusFilter) params.set("status", statusFilter);
    api.get(`/admin/leads${params.toString() ? `?${params}` : ""}`)
      .then((r) => setLeads(r.data?.results || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  };
  const loadTeam = () => {
    api.get("/admin/team-members").then((r) => setTeam((r.data || []).filter(m => m.status === "active")))
      .catch(() => setTeam([]));
  };
  useEffect(() => { loadAnalytics(); loadTeam(); loadTodayTasks(); }, []);
  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [tempFilter, statusFilter]);

  // v2.72 — Live sync + background refresh: lead lists and counters update
  // instantly after any lead/enquiry mutation (drawer save, delete, status
  // change, new enquiry via WS piggyback) and every 45 s while visible.
  const refreshAll = () => { loadLeads(true); loadAnalytics(); loadTodayTasks(); };
  useDataSync(["leads", "enquiries"], refreshAll);
  useAutoRefresh(refreshAll, 45000);

  // v2.48 — Bulk delete behavioural leads (deletes lead_profiles + auto_leads timeline)
  const bulkDeleteLeads = async (ids) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/leads/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} lead${r.data.deleted !== 1 ? "s" : ""}`);
      // v2.72 — Optimistic removal + bus announce (subscription reconciles).
      setLeads((prev) => prev.filter((l) => !ids.includes(l.user_id) && !ids.includes(l.id)));
      emitDataChange(["leads", "dashboard"]);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Bulk delete failed"));
    }
    setBulkBusy(false);
  };
  const deleteSingleLead = async (userId, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!window.confirm("Delete this lead profile? Activity timeline will be erased.")) return;
    try {
      await api.delete(`/admin/leads/${userId}`);
      toast.success("Lead deleted");
      // v2.72 — Optimistic removal + bus announce (subscription reconciles).
      setLeads((prev) => prev.filter((l) => l.user_id !== userId && l.id !== userId));
      emitDataChange(["leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err, "Failed")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-crm-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#0055FF] inline-flex items-center gap-1.5 mb-1">
            <Sparkles size={11} /> Behavioural CRM
          </div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">Smart Lead Intelligence</h2>
          <p className="text-[12.5px] text-zinc-600 mt-0.5">Auto-captured warm leads + form enquiries — assign to team, track to closure.</p>
        </div>
      </div>

      {/* v2.47 — Sub-tabs to merge behavioural leads + form enquiries */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-[#F4F4F5] rounded-sm p-1">
          <TabsTrigger value="behavioural" className="rounded-sm" data-testid="crm-subtab-behavioural">
            <Sparkles size={12} className="mr-1.5" /> Behavioural Leads
          </TabsTrigger>
          <TabsTrigger value="enquiries" className="rounded-sm" data-testid="crm-subtab-enquiries">
            <Inbox size={12} className="mr-1.5" /> Form Enquiries
          </TabsTrigger>
        </TabsList>

        <TabsContent value="behavioural" className="mt-5 space-y-6">

      {/* v2.70 Phase 2 — Today's Tasks banner */}
      {(todayTasks.total > 0) && (
        <div className="rounded-2xl border border-[#FCD34D] bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] p-4" data-testid="today-tasks-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#F59E0B] text-white">
                <Calendar size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#92400E]">
                  Today's Follow-ups
                </div>
                <div className="font-heading font-bold text-lg text-[#78350F]">
                  {todayTasks.total} action{todayTasks.total !== 1 ? "s" : ""} pending
                  {todayTasks.overdue.length > 0 && (
                    <span className="ml-2 text-[12px] font-bold text-[#DC2626]">
                      ({todayTasks.overdue.length} overdue)
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 max-w-full">
              {[...todayTasks.overdue, ...todayTasks.today].slice(0, 4).map((t) => (
                <button
                  key={t.user_id}
                  onClick={() => setSelectedUserId(t.user_id)}
                  data-testid={`today-task-${t.user_id}`}
                  className="bg-white border border-[#FCD34D] hover:border-[#F59E0B] rounded-md px-2.5 py-1.5 text-left transition-all hover:shadow-sm"
                >
                  <div className="text-[11px] font-bold text-zinc-800 truncate max-w-[160px]">
                    {t.user_name || t.user_email || "Lead"}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">
                    {t.next_action_text || t.next_action_at?.slice(0, 16).replace("T", " ")}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Analytics — temperature + funnel + top areas */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(TEMP_META).map(([key, m]) => {
              const count = analytics.temperature?.[key] ?? 0;
              const TempIcon = m.Icon;
              const isActive = tempFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setTempFilter(isActive ? "" : key)}
                  className={`relative bg-white border rounded-2xl p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${isActive ? "border-[#0055FF] ring-2 ring-[#0055FF]/20" : "border-zinc-200"}`}
                  data-testid={`temp-card-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${m.color}18`, color: m.color }}>
                      <TempIcon size={16} />
                    </div>
                    <span className="font-heading font-bold text-2xl tracking-tight" style={{ color: m.color }}>{count}</span>
                  </div>
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-700 mt-2">{m.label}</div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">leads</div>
                </button>
              );
            })}
          </div>

          <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="status-funnel-card">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-700 inline-flex items-center gap-1.5 mb-3">
              <Activity size={11} /> Status Funnel
            </div>
            <div className="space-y-1.5">
              {Object.entries(STATUS_META).map(([key, m]) => {
                const count = analytics.status_funnel?.[key] ?? 0;
                const total = Math.max(1, analytics.total_leads || 0);
                const pct = Math.round((count / total) * 100);
                const isActive = statusFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(isActive ? "" : key)}
                    className={`w-full flex items-center gap-2 text-left ${isActive ? "scale-[1.01]" : ""}`}
                    data-testid={`funnel-row-${key}`}
                  >
                    <div className={`text-[11px] uppercase tracking-wider font-bold w-24 truncate ${isActive ? "text-[#0055FF]" : "text-zinc-700"}`}>{m.label}</div>
                    <div className="flex-1 relative h-5 bg-zinc-100 rounded-md overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0055FF] to-[#2D7BFF]" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 inline-flex items-center justify-center text-[10.5px] font-bold text-zinc-700 mix-blend-difference">{count}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Top areas + listings */}
      {analytics && (analytics.top_areas?.length > 0 || analytics.top_listings?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="top-areas-card">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-700 inline-flex items-center gap-1.5 mb-3">
              <MapPin size={11} className="text-[#0055FF]" /> Hot Areas (last 30 days)
            </div>
            {analytics.top_areas?.length ? (
              <div className="flex flex-wrap gap-2">
                {analytics.top_areas.map((a) => (
                  <span key={a.area} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EEF4FF] border border-[#C7D9FF] text-[12px] font-bold text-[#0044CC]">
                    {a.area} <span className="text-[#0055FF]">· {a.count}</span>
                  </span>
                ))}
              </div>
            ) : <div className="text-[12px] text-zinc-500">No area signals yet.</div>}
          </div>

          <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid="top-listings-card">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-700 inline-flex items-center gap-1.5 mb-3">
              <Eye size={11} className="text-[#0055FF]" /> Most-Viewed Listings (last 30 days)
            </div>
            {analytics.top_listings?.length ? (
              <ul className="space-y-1.5 text-[12.5px]">
                {analytics.top_listings.map((l) => (
                  // v2.74 — Clickable cards: open the property detail in the
                  // SAME tab (Link, not anchor target=_blank) so navigation
                  // history is preserved. Back button returns to /admin/crm
                  // with React Router's default scroll restoration (browser
                  // keeps the scroll position on history pop). No target=_blank
                  // here — explicit user requirement: same-tab navigation.
                  <li key={l.id}>
                    <Link
                      to={`/property/${l.id}`}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 -mx-2 rounded-md hover:bg-[#EEF4FF] focus:bg-[#EEF4FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]/30 transition-colors cursor-pointer"
                      data-testid={`crm-top-listing-link-${l.id}`}
                      title={`Open ${l.title || l.id}`}
                    >
                      <span className="truncate text-zinc-800 hover:text-[#0055FF]">{l.title || l.id}</span>
                      <span className="font-mono-data text-[#0055FF] font-bold whitespace-nowrap">{l.views} views</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : <div className="text-[12px] text-zinc-500">No views yet.</div>}
          </div>
        </div>
      )}

      {/* Active filters */}
      {(tempFilter || statusFilter) && (
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className="text-zinc-500">Filtering:</span>
          {tempFilter && (
            <button onClick={() => setTempFilter("")} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold" data-testid="clear-temp-filter">
              {TEMP_META[tempFilter].label} <X size={10} />
            </button>
          )}
          {statusFilter && (
            <button onClick={() => setStatusFilter("")} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold" data-testid="clear-status-filter">
              {STATUS_META[statusFilter].label} <X size={10} />
            </button>
          )}
        </div>
      )}

      {/* Lead list */}
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid="leads-list">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-700">Leads ({leads.length})</div>
          <button onClick={loadLeads} className="text-[11.5px] font-bold text-[#0055FF] hover:underline">Refresh</button>
        </div>
        {/* v2.48 — Bulk select */}
        {leads.length > 0 && (
          <div className="px-4 pt-3">
            <BulkSelectBar
              selectedIds={selectedLeadIds}
              setSelectedIds={setSelectedLeadIds}
              allIds={leads.map((l) => l.user_id)}
              onBulkDelete={bulkDeleteLeads}
              busy={bulkBusy}
              noun="leads"
              testidPrefix="leads-bulk"
            />
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-[13px] text-zinc-500">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center" data-testid="leads-empty">
            <Sparkles size={20} className="mx-auto text-zinc-400 mb-2" />
            <div className="text-[13.5px] font-bold text-zinc-800">No leads matching this filter</div>
            <div className="text-[12px] text-zinc-500 mt-1">Try widening the filter or wait for more user activity.</div>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {leads.map((l) => {
              const t = TEMP_META[l.temperature] || TEMP_META.cold;
              const s = STATUS_META[l.status] || STATUS_META.new;
              const TempIcon = t.Icon;
              return (
                <li key={l.user_id}>
                  <div className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50">
                    <RowCheckbox
                      id={l.user_id}
                      selectedIds={selectedLeadIds}
                      setSelectedIds={setSelectedLeadIds}
                      testid={`lead-row-check-${l.user_id}`}
                    />
                    <button
                      onClick={() => setSelectedUserId(l.user_id)}
                      className="flex-1 flex items-center gap-3 text-left min-w-0"
                      data-testid={`lead-row-${l.user_id}`}
                    >
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${t.color}18`, color: t.color }}>
                        <TempIcon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[13.5px] text-zinc-900 truncate">{l.user_name || l.user_email}</span>
                          <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 border rounded-full ${t.chip}`}>
                            {t.label}
                          </span>
                          <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 border rounded-full ${s.chip}`}>
                            {s.label}
                          </span>
                        </div>
                        <div className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
                          {l.user_email} · {l.user_phone || "no phone"} · {(l.preferred_areas || []).slice(0, 3).join(", ") || "no area pref"}
                        </div>
                        {l.assigned_to && (() => {
                          const m = team.find((x) => x.id === l.assigned_to);
                          return (
                            <div className="text-[10.5px] text-[#0044CC] font-bold mt-0.5 inline-flex items-center gap-1" data-testid={`lead-row-${l.user_id}-assigned`}>
                              <UserCog size={10} /> {m ? `${m.name} · ${m.sub_role}` : "Assigned"}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-right shrink-0 hidden sm:block">
                        <div className="font-mono-data font-bold text-[#0055FF] text-[14px]">{l.score} pts</div>
                        <div className="text-[10.5px] text-zinc-500">{l.events_count || 0} events</div>
                      </div>
                      <ChevronRight size={16} className="text-zinc-400 shrink-0" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => deleteSingleLead(l.user_id, e)}
                      className="shrink-0 p-1.5 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                      title="Delete lead"
                      data-testid={`lead-delete-${l.user_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      {selectedUserId && (
        <LeadDetailDrawer
          userId={selectedUserId}
          team={team}
          onClose={() => { setSelectedUserId(null); loadLeads(); loadAnalytics(); }}
        />
      )}
        </TabsContent>

        {/* v2.47 — Form Enquiries sub-tab (merged from old Leads tab) */}
        <TabsContent value="enquiries" className="mt-5">
          <AdminEnquiriesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LeadDetailDrawer({ userId, team = [], onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [assignDraft, setAssignDraft] = useState("");
  // v2.70 Phase 2 — intent + next-action drafts
  const [intentDraft, setIntentDraft] = useState("sale");
  const [nextActionAt, setNextActionAt] = useState("");
  const [nextActionText, setNextActionText] = useState("");
  const [stagesConfig, setStagesConfig] = useState({ sale: [], rent: [], lease: [] });

  // Fetch canonical stage config once
  useEffect(() => {
    api.get("/leads/stages-config")
      .then((r) => setStagesConfig(r.data?.stages || { sale: [], rent: [], lease: [] }))
      .catch(() => {});
  }, []);

  const load = () => {
    api.get(`/admin/leads/${userId}`).then((r) => {
      setData(r.data);
      setStatusDraft(r.data?.profile?.status || "new");
      setAssignDraft(r.data?.profile?.assigned_to || "");
      setIntentDraft(r.data?.profile?.intent || "sale");
      setNextActionAt(r.data?.profile?.next_action_at?.slice(0, 16) || "");
      setNextActionText(r.data?.profile?.next_action_text || "");
    });
  };
  useEffect(load, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (statusDraft !== data?.profile?.status) payload.status = statusDraft;
      if ((assignDraft || "") !== (data?.profile?.assigned_to || "")) payload.assigned_to = assignDraft || null;
      if (intentDraft !== (data?.profile?.intent || "sale")) payload.intent = intentDraft;
      // next-action — always send if changed (or being cleared)
      const oldAt = data?.profile?.next_action_at?.slice(0, 16) || "";
      const oldText = data?.profile?.next_action_text || "";
      if (nextActionAt !== oldAt) payload.next_action_at = nextActionAt ? new Date(nextActionAt).toISOString() : "";
      if (nextActionText !== oldText) payload.next_action_text = nextActionText;
      if (note.trim()) payload.note = note.trim();
      if (Object.keys(payload).length === 0) { setBusy(false); return; }
      await api.patch(`/admin/leads/${userId}`, payload);
      toast.success("Lead updated");
      setNote("");
      load();
      // v2.72 — Parent list/analytics refresh instantly behind the drawer.
      emitDataChange(["leads", "dashboard"]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update lead");
    }
    setBusy(false);
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="lead-drawer">
        <div className="text-white">Loading…</div>
      </div>
    );
  }
  const p = data.profile;
  const t = TEMP_META[p.temperature] || TEMP_META.cold;

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-stretch justify-end" data-testid="lead-drawer">
      <div className="w-full sm:w-[520px] max-w-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-500">Lead profile</div>
            <h3 className="font-heading font-bold text-lg text-zinc-900 truncate max-w-xs">{p.user_name || p.user_email}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-zinc-100 inline-flex items-center justify-center" data-testid="lead-drawer-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Score + temperature */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${t.color}18`, color: t.color }}>
              <span className="font-heading font-bold text-xl">{p.score}</span>
            </div>
            <div>
              <div className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold px-2 py-0.5 border rounded-full ${t.chip}`}>
                <t.Icon size={11} /> {t.label}
              </div>
              <div className="text-[12px] text-zinc-600 mt-1">{p.events_count || 0} events · last active {p.last_active_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3 text-[12.5px]">
            <Stat label="Email" value={p.user_email} />
            <Stat label="Phone" value={p.user_phone || "—"} />
            <Stat label="Role" value={p.user_role} />
            <Stat label="Last event" value={p.last_event || "—"} />
          </div>

          {/* Intent */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3.5" data-testid="lead-intent">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Intent profile</div>
            <Row label="Areas">{(p.preferred_areas || []).join(", ") || "—"}</Row>
            <Row label="BHK">{(p.preferred_bhk || []).join(", ") || "—"}</Row>
            <Row label="Property type">{(p.preferred_property_types || []).join(", ") || "—"}</Row>
            <Row label="Saves / Views / Enquiries">{p.saves_count || 0} / {p.views_count || 0} / {p.enquiries_count || 0}</Row>
          </div>

          {/* v2.70 Phase 2 — Intent selector */}
          <div data-testid="lead-intent-selector">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Listing Intent</div>
            <div className="inline-flex bg-zinc-50 border border-zinc-200 rounded-full p-1">
              {["sale", "rent", "lease"].map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setIntentDraft(k);
                    // Reset status to first stage if current status not valid for new intent
                    const stages = stagesConfig[k] || [];
                    if (!stages.includes(statusDraft)) setStatusDraft(stages[0] || "inquiry");
                  }}
                  data-testid={`lead-intent-${k}`}
                  className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full transition-all ${
                    intentDraft === k ? "bg-[#09090B] text-white shadow-sm" : "text-zinc-600 hover:text-zinc-900"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          {/* v2.70 Phase 2 — Stage selector (intent-aware, ordered as a progress bar) */}
          <div data-testid="lead-status-edit">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Pipeline Stage</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {(stagesConfig[intentDraft] || []).map((stage, idx) => {
                const isActive = statusDraft === stage;
                const isLost = stage === "lost";
                return (
                  <button
                    key={stage}
                    onClick={() => setStatusDraft(stage)}
                    className={`text-[11px] font-bold px-2 py-1.5 border rounded-md transition-all ${
                      isActive
                        ? (isLost ? "border-[#DC2626] bg-[#FEE2E2] text-[#991B1B]" : "border-[#0055FF] bg-[#EEF4FF] text-[#0044CC]")
                        : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                    }`}
                    data-testid={`lead-stage-${stage}`}
                  >
                    {!isLost && <span className="text-[9px] mr-1 text-zinc-400">{idx + 1}.</span>}
                    {stage.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>

          {/* v2.70 Phase 2 — Next action (date + note) */}
          <div data-testid="lead-next-action" className="bg-[#FFFBEB] border border-[#FCD34D] rounded-xl p-3.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#92400E] mb-2 flex items-center gap-1.5">
              <Calendar size={11} /> Next Action / Follow-up
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                type="datetime-local"
                value={nextActionAt}
                onChange={(e) => setNextActionAt(e.target.value)}
                data-testid="lead-next-action-at"
                className="w-full h-9 px-2 text-[12.5px] border border-[#FDE68A] rounded-md bg-white focus:outline-none focus:border-[#D97706]"
              />
              <input
                type="text"
                placeholder="e.g. Call back for site-visit"
                value={nextActionText}
                onChange={(e) => setNextActionText(e.target.value)}
                data-testid="lead-next-action-text"
                className="w-full h-9 px-3 text-[12.5px] border border-[#FDE68A] rounded-md bg-white focus:outline-none focus:border-[#D97706]"
              />
            </div>
            {data?.profile?.next_action_at && (
              <div className="text-[10.5px] text-[#92400E] mt-2">
                Saved: {data.profile.next_action_at.slice(0, 16).replace("T", " ")}{data.profile.next_action_text ? ` — ${data.profile.next_action_text}` : ""}
              </div>
            )}
          </div>

          {/* v2.47 — Assign to team member */}
          <div data-testid="lead-assign-team">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2 flex items-center gap-1.5">
              <UserCog size={11} /> Assign to team member
            </div>
            {team.length === 0 ? (
              <div className="text-[12px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                No active team members. Add one from <b>Team</b> tab.
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  value={assignDraft || ""}
                  onChange={(e) => setAssignDraft(e.target.value)}
                  className="flex-1 h-9 px-3 border border-zinc-200 rounded-md text-[12.5px] bg-white font-semibold"
                  data-testid="lead-assign-select"
                >
                  <option value="">— Unassigned —</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>{`${m.name} · ${m.sub_role} · ${m.mobile}`}</option>
                  ))}
                </select>
              </div>
            )}
            {assignDraft && (() => {
              const m = team.find((x) => x.id === assignDraft);
              return m ? (
                <div className="text-[11px] text-[#0044CC] mt-1.5" data-testid="lead-assign-current">
                  Will be assigned to: <b>{m.name}</b> ({m.sub_role})
                </div>
              ) : null;
            })()}
          </div>

          {/* Notes */}
          <div data-testid="lead-add-note">
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Add note</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Customer is shortlisting in Dwarka…" data-testid="lead-note-input" />
            {(p.notes || []).length > 0 && (
              <div className="mt-3 space-y-2">
                {(p.notes || []).slice().reverse().slice(0, 5).map((n, i) => (
                  <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5 text-[12px]">
                    <div className="text-[10px] text-zinc-500">{n.by} · {n.at?.slice(0, 16).replace("T", " ")}</div>
                    <div className="text-zinc-800 mt-0.5">{n.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline — v2.69: split into Buyer Activity + Internal Actions */}
          {(() => {
            const buyerEvents = data.timeline.filter((e) => !(EVENT_META[e.kind]?.internal));
            const internalEvents = data.timeline.filter((e) => EVENT_META[e.kind]?.internal);
            const renderEventRow = (e) => {
              const m = EVENT_META[e.kind] || { Icon: Activity, color: "#71717A", label: e.kind };
              const actorName = e.meta?.actor_name;
              const actorRole = e.meta?.actor_role;
              return (
                <li key={e.id} className="flex items-start gap-2 text-[12px]">
                  <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${m.color}15`, color: m.color }}>
                    <m.Icon size={12} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-zinc-800">{m.label} <span className="font-mono-data text-zinc-400 font-normal">· {formatTime(e.created_at)}</span></div>
                    {(e.meta?.title || e.meta?.name) && <div className="text-zinc-600 truncate">{e.meta.title || e.meta.name}{e.meta.area ? ` · ${e.meta.area}` : ""}</div>}
                    {e.meta?.status && <div className="text-zinc-500 text-[11px]">Status → <span className="font-semibold">{e.meta.status}</span></div>}
                    {e.meta?.text && <div className="text-zinc-700 text-[11.5px] italic truncate">"{e.meta.text}"</div>}
                    {actorName && <div className="text-zinc-400 text-[10.5px] uppercase tracking-wider">By {actorName}{actorRole ? ` · ${actorRole}` : ""}</div>}
                  </div>
                </li>
              );
            };
            return (
              <>
                <div data-testid="lead-timeline">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2 flex items-center gap-1.5">
                    <Calendar size={11} /> Buyer Activity ({buyerEvents.length})
                  </div>
                  {buyerEvents.length === 0 ? (
                    <div className="text-[11.5px] text-zinc-400 italic py-2">No buyer activity yet.</div>
                  ) : (
                    <ul className="space-y-2 max-h-72 overflow-y-auto">{buyerEvents.map(renderEventRow)}</ul>
                  )}
                </div>
                <div data-testid="lead-internal-actions" className="mt-5">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2 flex items-center gap-1.5">
                    <UserCog size={11} /> Internal Actions ({internalEvents.length})
                  </div>
                  {internalEvents.length === 0 ? (
                    <div className="text-[11.5px] text-zinc-400 italic py-2">No internal actions logged yet.</div>
                  ) : (
                    <ul className="space-y-2 max-h-72 overflow-y-auto">{internalEvents.map(renderEventRow)}</ul>
                  )}
                </div>
              </>
            );
          })()}
        </div>

        <div className="px-5 py-3.5 border-t border-zinc-200 flex items-center justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="lead-drawer-cancel">Close</Button>
          <Button onClick={save} disabled={busy} className="btn-gradient rounded-xl" data-testid="lead-drawer-save">{busy ? "Saving…" : "Save Changes"}</Button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-0.5">{label}</div>
    <div className="text-zinc-900 font-bold truncate">{value}</div>
  </div>
);
const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-2 text-[12.5px] py-0.5">
    <span className="text-zinc-500 w-32 shrink-0 text-[11px] uppercase tracking-wider font-bold">{label}</span>
    <span className="text-zinc-800 truncate">{children}</span>
  </div>
);
